import { css } from '@emotion/react'
import {
    memo,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { VListHandle } from 'virtua'
import {
    fetchImageCategory,
    IUserImage,
    searchImage,
} from '../../../../mfcLogic/api/chatImages'
import { openGifInsertionConfirmation } from '../../../../state/modals/state.confirmation'
import i18n from '../../../../utilities/i18n'
import { LoadingData } from '../../../common/LoadingData'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, {
    maxWindowWidth,
    smallHeightDialogCheck,
} from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import { greenMFC, PAPER_HEIGHTS } from '../../../theme'
import GridVirtual from '../../GridVirtual'
import InputSearch from '../../InputSearch'
import NoResults from '../../Search/NoResults'
import BrowseCategories from './BrowseCategories'
import SmallGif from './SmallGif'

const noResultsStyles = css({
    height: 428,
    padding: getThemeSpacing(2),
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        height: 198,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    '@media screen and (max-height: 412px)': {
        height: 133,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
    [smallHeightDialogCheck]: {
        height: 160,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
})

const gridRootStyles = css({
    height: 428,
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        height: 223,
    },
    '@media screen and (max-height: 412px)': {
        height: 133,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
    [smallHeightDialogCheck]: {
        height: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 0,
    },
    zIndex: PAPER_HEIGHTS.base,
    flex: '0 1 auto',
    padding: `0 ${getThemeSpacing(2)}`,
    background: palette.dialogContrastBackground,
})

interface IGifs {
    initialValue: string
    onInsertImage(imageData: IUserImage): void
    onQueryUpdate(query: string): void
    showSearch?: boolean
}

const Gifs = memo((props: IGifs) => {
    const {
        initialValue,
        onInsertImage = () => {},
        onQueryUpdate,
        showSearch = true,
    } = props

    const [showBrowseCategories, setShowBrowseCategories] = useState(true)
    const [images, setImages] = useState<Array<IUserImage>>([])
    const [loading, setLoading] = useState(true)

    const searchQuery = useRef('')
    const showNoResults = images.length === 0

    const cachedCategoryImages = useRef([])
    const cachedSearchImages = useRef([])

    const [selectedCategory, setSelectedCategory] = useState('general')
    const [sortCategoryBy, setSortCategoryBy] = useState('usage')
    const [searchType, setSearchType] = useState('shortcut')
    const [sortSearchBy, setSortSearchBy] = useState('frequency')

    const onUpdateList = useCallback((ref: RefObject<VListHandle>) => {
        ref.current?.scrollTo(0)
    }, [])

    const onClickSmallGif = useCallback(
        (imageData: IUserImage) => {
            openGifInsertionConfirmation(() => {
                const shortcut = imageData?.shortcut
                if (!shortcut || shortcut === '') return
                onInsertImage(imageData)
            }, imageData)
        },
        [onInsertImage],
    )

    const updateFromCategories = useCallback(
        async (category: string, sortBy: string) => {
            const { current: cachedCategory } = cachedCategoryImages
            searchQuery.current = ''

            if (
                category === selectedCategory &&
                sortBy === sortCategoryBy &&
                cachedCategory.length !== 0
            ) {
                setImages([...cachedCategory])
                setShowBrowseCategories(true)
            } else {
                setLoading(true)
                const images = await fetchImageCategory(category, sortBy)
                cachedCategoryImages.current = images
                setImages([...images])
                setLoading(false)
                setShowBrowseCategories(true)
            }
        },
        [selectedCategory, sortCategoryBy],
    )

    const handleSearchGIF = useCallback(
        async (query = '', type = searchType, sortBy = sortSearchBy) => {
            onQueryUpdate(query)
            if (query === '') {
                return updateFromCategories(selectedCategory, sortCategoryBy)
            }

            const { current: currentQuery } = searchQuery
            const { current: cachedSearch } = cachedSearchImages

            if (
                query === currentQuery &&
                type === searchType &&
                sortBy === sortSearchBy &&
                cachedSearch.length !== 0
            ) {
                setImages([...cachedSearch])
                setShowBrowseCategories(false)
            } else {
                searchQuery.current = query
                setLoading(true)
                const images = await searchImage(query, type, sortBy)
                cachedSearchImages.current = images
                setImages([...images])
                setLoading(false)
                setShowBrowseCategories(false)
            }
        },
        [
            onQueryUpdate,
            updateFromCategories,
            selectedCategory,
            sortCategoryBy,
            searchType,
            sortSearchBy,
        ],
    )

    useEffect(() => {
        handleSearchGIF(initialValue)
    }, [])
    const handleFilterClick = useCallback((_event: any, isActive = false) => {
        setShowBrowseCategories(isActive)
    }, [])

    const onSelectCategory = useCallback(
        (category: string) => {
            void updateFromCategories(category, sortCategoryBy)
            setSelectedCategory(category)
        },
        [updateFromCategories, sortCategoryBy],
    )

    const onSelectSortBy = useCallback(
        (sortBy: string) => {
            void updateFromCategories(selectedCategory, sortBy)
            setSortCategoryBy(sortBy)
        },
        [updateFromCategories, selectedCategory],
    )

    const onSelectSearchType = useCallback(
        (type: string) => {
            const { current } = searchQuery
            if (current !== '') {
                void handleSearchGIF(current, type, sortSearchBy)
            }
            setSearchType(type)
        },
        [handleSearchGIF, sortSearchBy],
    )

    const onSelectSortSearchBy = useCallback(
        (searchBy: string) => {
            const { current } = searchQuery
            if (current !== '') {
                void handleSearchGIF(current, searchType, searchBy)
            }
            setSortSearchBy(searchBy)
        },
        [handleSearchGIF, searchType],
    )

    return (
        <>
            {showSearch && (
                <InputSearch
                    defaultValue={initialValue}
                    isFilterActive={showBrowseCategories}
                    onChange={handleSearchGIF}
                    onClickFilter={handleFilterClick}
                    placeholder={i18n.t('chatImages.placeholderSearchGIF')}
                    clearInputIcon="closeCircle"
                    clearInputIconColor={greenMFC}
                    quietFocus
                />
            )}
            <BrowseCategories
                showBrowseCategories={showBrowseCategories}
                onSelectCategory={onSelectCategory}
                onSelectSortBy={onSelectSortBy}
                onSelectSearchType={onSelectSearchType}
                onSelectSortSearchBy={onSelectSortSearchBy}
                selectedCategory={selectedCategory}
                sortCategoryBy={sortCategoryBy}
                searchType={searchType}
                sortSearchBy={sortSearchBy}
            />
            {loading ? (
                <LoadingData active />
            ) : showNoResults ? (
                <NoResults
                    styleOverrides={noResultsStyles}
                    searchQuery={initialValue}
                />
            ) : (
                <GridVirtual
                    styleOverrides={gridRootStyles}
                    columns={4}
                    list={images}
                    onUpdateList={onUpdateList}
                    testId="EmojiGif__Images"
                >
                    <SmallGif onClickGif={onClickSmallGif} />
                </GridVirtual>
            )}
        </>
    )
})

export default Gifs
